import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Stethoscope, X, ChevronLeft, ShieldAlert } from 'lucide-react';
import { apiClient } from '../lib/api';
import { useUser } from '../store/useAuthStore';
import type { TelemedNetwork, TelemedProvider } from '@bloom/shared';

// Admin management for telemed networks — the roster of real prescribers and
// their per-state licenses that clinic accounts use for prescribing.

type EditableNetwork = Pick<TelemedNetwork, 'name' | 'isActive' | 'perVisitFeeCents'> & {
  messagingRoute: 'clinic_inbox' | 'core';
  _id?: string;
  providers: TelemedProvider[];
};

const blankNetwork = (): EditableNetwork => ({
  name: '',
  isActive: true,
  perVisitFeeCents: 2500,
  messagingRoute: 'clinic_inbox',
  providers: []
});

export const TelemedNetworks: React.FC = () => {
  const user = useUser();
  const hasPermission = user?.role === 'admin' && (user as any)?.adminData?.canManageTelemedNetworks === true;
  const [networks, setNetworks] = useState<TelemedNetwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditableNetwork | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Feature is gated by the per-admin canManageTelemedNetworks permission. The
  // backend enforces this too — this is the UI side of the same gate.
  if (user && !hasPermission) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-6 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-900">Telemed-network access not enabled</div>
            <div className="text-sm text-gray-600 mt-1">
              A superadmin needs to grant your account the "Can manage telemed networks" permission before you can use this feature.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const load = async () => {
    setLoading(true);
    const res = await apiClient.getTelemedNetworks();
    setLoading(false);
    if (res.success && res.data) setNetworks(res.data);
    else setError(res.error || 'Failed to load networks');
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setError(null); setEditing(blankNetwork()); };
  const startEdit = (n: TelemedNetwork) => {
    setError(null);
    setEditing({ _id: n._id, name: n.name, isActive: n.isActive, perVisitFeeCents: n.perVisitFeeCents, messagingRoute: n.messagingRoute ?? 'clinic_inbox', providers: n.providers || [] });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    const body = {
      name: editing.name.trim(),
      isActive: editing.isActive,
      perVisitFeeCents: editing.perVisitFeeCents,
      messagingRoute: editing.messagingRoute,
      providers: editing.providers
    };
    const res = editing._id
      ? await apiClient.updateTelemedNetwork(editing._id, body as any)
      : await apiClient.createTelemedNetwork(body as any);
    setSaving(false);
    if (res.success) { setEditing(null); load(); }
    else setError(res.error || 'Failed to save network');
  };

  const remove = async (n: TelemedNetwork) => {
    if (!window.confirm(`Delete network "${n.name}"? This cannot be undone.`)) return;
    const res = await apiClient.deleteTelemedNetwork(n._id);
    if (res.success) load();
    else window.alert(res.error || 'Failed to delete');
  };

  // ---- Provider/license editing helpers (operate on the editing draft) ----
  const updateProviders = (providers: TelemedProvider[]) => setEditing(e => e ? { ...e, providers } : e);
  const addProvider = () => editing && updateProviders([...editing.providers, { name: '', npi: '', licenses: [] }]);
  const removeProvider = (i: number) => editing && updateProviders(editing.providers.filter((_, idx) => idx !== i));
  const setProvider = (i: number, patch: Partial<TelemedProvider>) =>
    editing && updateProviders(editing.providers.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const addLicense = (pi: number) =>
    editing && setProvider(pi, { licenses: [...editing.providers[pi].licenses, { state: '', number: '' }] });
  const setLicense = (pi: number, li: number, patch: Partial<{ state: string; number: string }>) => {
    if (!editing) return;
    const licenses = editing.providers[pi].licenses.map((l, idx) => idx === li ? { ...l, ...patch } : l);
    setProvider(pi, { licenses });
  };
  const removeLicense = (pi: number, li: number) => {
    if (!editing) return;
    setProvider(pi, { licenses: editing.providers[pi].licenses.filter((_, idx) => idx !== li) });
  };

  const stateCount = (n: TelemedNetwork) =>
    new Set((n.providers || []).flatMap(p => p.licenses.map(l => l.state.toUpperCase()))).size;

  if (editing) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to networks
        </button>
        <h1 className="text-xl font-semibold mb-4">{editing._id ? 'Edit network' : 'New network'}</h1>

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Network name</label>
            <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="e.g. Nationwide Telemed Group" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fee per visit (USD)</label>
            <input type="number" min={0} step="0.01"
              value={(editing.perVisitFeeCents / 100).toString()}
              onChange={e => setEditing({ ...editing, perVisitFeeCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm mb-6">
          <input type="checkbox" checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />
          Active (clinics can be assigned to this network)
        </label>

        <div className="mb-6 max-w-xl">
          <label className="block text-xs font-medium text-gray-500 mb-1">Patient messages to this network's prescribers</label>
          <select value={editing.messagingRoute} onChange={e => setEditing({ ...editing, messagingRoute: e.target.value as EditableNetwork['messagingRoute'] })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
            <option value="clinic_inbox">Clinic's care inbox</option>
            <option value="core">5 Axis Core (the network's doctors answer there)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">Core routing applies once Core messaging is configured for this deployment; until then messages stay in the clinic's inbox.</p>
        </div>

        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-700">Providers & state licenses</h2>
          <button onClick={addProvider} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <Plus className="h-4 w-4" /> Add provider
          </button>
        </div>

        <div className="space-y-4">
          {editing.providers.length === 0 && (
            <p className="text-sm text-gray-400">No providers yet. Add the doctors whose licenses cover your states.</p>
          )}
          {editing.providers.map((p, pi) => (
            <div key={pi} className="border border-gray-200 rounded-md p-4">
              <div className="flex items-start gap-3 mb-3">
                <input value={p.name} onChange={e => setProvider(pi, { name: e.target.value })}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Provider name" />
                <input value={p.npi} onChange={e => setProvider(pi, { npi: e.target.value })}
                  className="w-40 border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="NPI" />
                <button onClick={() => removeProvider(pi)} className="p-2 text-gray-400 hover:text-red-600" title="Remove provider">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="pl-1 space-y-2">
                {p.licenses.map((l, li) => (
                  <div key={li} className="flex items-center gap-2">
                    <input value={l.state} maxLength={2}
                      onChange={e => setLicense(pi, li, { state: e.target.value.toUpperCase() })}
                      className="w-16 border border-gray-300 rounded-md px-2 py-1.5 text-sm uppercase" placeholder="ST" />
                    <input value={l.number} onChange={e => setLicense(pi, li, { number: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm" placeholder="License number" />
                    <button onClick={() => removeLicense(pi, li)} className="p-1.5 text-gray-400 hover:text-red-600" title="Remove license">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button onClick={() => addLicense(pi)} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                  <Plus className="h-3.5 w-3.5" /> Add state license
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save network'}
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-gray-500" /> Telemed Networks
        </h1>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New network
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : networks.length === 0 ? (
        <div className="text-sm text-gray-400">No telemed networks yet. Create one to assign clinics to it.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-100">
          {networks.map(n => (
            <div key={n._id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="font-medium text-gray-900 flex items-center gap-2">
                  {n.name}
                  {!n.isActive && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>}
                </div>
                <div className="text-xs text-gray-500">
                  {n.providers?.length || 0} provider(s) · {stateCount(n)} state(s) · ${(n.perVisitFeeCents / 100).toFixed(2)}/visit
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(n)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Edit</button>
                <button onClick={() => remove(n)} className="p-2 text-gray-400 hover:text-red-600" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TelemedNetworks;
